/**
 * AddManualEventModal Component
 *
 * Modal for adding manual notes, milestones, and photos to project timeline.
 * Unified entry point for all bitacora entries.
 *
 * @module components/timeline/AddManualEventModal
 */

import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Alert, Image } from 'react-bootstrap';
import { addManualEvent } from '../../services/timelineService';
import { uploadProgressPhotos } from '../../services/progressService';
import { toast } from '../../utils/toast';

/**
 * AddManualEventModal component
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Callback to close modal
 * @param {string} props.projectId - Project ID
 * @param {Function} props.onSuccess - Callback after successful addition
 */
const AddManualEventModal = ({ show, onHide, projectId, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    description: '',
    changeType: 'manual_note',
    isMilestone: false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten archivos de imagen');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen no puede superar 10MB');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate: need either description or photo
    if (!formData.description.trim() && !selectedFile) {
      setError('Debes agregar una descripcion o una foto');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Case 1: Photo with optional caption -> goes to progress_photos
      if (selectedFile) {
        setUploadProgress(30);

        const uploadResult = await uploadProgressPhotos(
          projectId,
          [selectedFile],
          formData.description.trim() || null, // Caption
          null, // GPS coords (could add later)
          null  // Task ID
        );

        setUploadProgress(80);

        if (uploadResult.error) throw uploadResult.error;
        if (uploadResult.errors?.length > 0) {
          throw new Error(uploadResult.errors[0].error);
        }

        // If it's a milestone, also add to project_history
        if (formData.isMilestone || formData.changeType === 'milestone_reached') {
          await addManualEvent(projectId, {
            description: formData.description.trim() || 'Foto de progreso subida',
            changeType: 'milestone_reached',
            isMilestone: true,
          });
        }

        setUploadProgress(100);
        toast.success('Foto agregada a la bitácora');
      }
      // Case 2: Text only -> goes to project_history
      else {
        const result = await addManualEvent(projectId, formData);
        if (result.error) throw result.error;
        toast.success('Nota agregada a la bitácora');
      }

      // Reset form
      setFormData({
        description: '',
        changeType: 'manual_note',
        isMilestone: false,
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onSuccess?.();
      onHide();
    } catch (err) {
      console.error('Error adding to bitacora:', err);
      setError(err.message || 'Error al agregar la entrada');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        description: '',
        changeType: 'manual_note',
        isMilestone: false,
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onHide();
    }
  };

  const canSubmit = (formData.description.trim() || selectedFile) && !isSubmitting;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-plus-circle me-2"></i>
          Agregar a Bitácora
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* Photo Upload */}
          <Form.Group className="mb-3">
            <Form.Label>
              <i className="bi bi-camera me-2"></i>
              Foto (opcional)
            </Form.Label>

            {!previewUrl ? (
              <div
                className="border rounded p-4 text-center bg-light"
                style={{ cursor: 'pointer' }}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="bi bi-cloud-upload fs-1 text-muted d-block mb-2"></i>
                <p className="mb-1">Haz clic para seleccionar una imagen</p>
                <small className="text-muted">JPG, PNG o GIF - Max 10MB</small>
              </div>
            ) : (
              <div className="position-relative">
                <Image
                  src={previewUrl}
                  alt="Preview"
                  fluid
                  rounded
                  className="mb-2"
                  style={{ maxHeight: '200px', objectFit: 'contain' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleRemoveFile}
                  disabled={isSubmitting}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </div>
            )}

            <Form.Control
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={isSubmitting}
              className="d-none"
            />
          </Form.Group>

          {/* Description / Caption */}
          <Form.Group className="mb-3">
            <Form.Label>
              {selectedFile ? 'Descripción de la foto' : 'Descripción'}
              {!selectedFile && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={selectedFile
                ? "Describe que muestra esta foto (opcional)..."
                : "Describe el evento o nota..."}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={isSubmitting}
              maxLength={500}
            />
            <Form.Text className="text-muted">
              {formData.description.length}/500 caracteres
            </Form.Text>
          </Form.Group>

          {/* Entry Type (only for text entries) */}
          {!selectedFile && (
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Entrada</Form.Label>
              <Form.Select
                value={formData.changeType}
                onChange={(e) => handleChange('changeType', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="manual_note">Nota</option>
                <option value="milestone_reached">Hito Alcanzado</option>
                <option value="custom_update">Actualizacion</option>
              </Form.Select>
            </Form.Group>
          )}

          {/* Milestone checkbox */}
          <Form.Group>
            <Form.Check
              type="checkbox"
              id="is-milestone"
              label={
                <>
                  <i className="bi bi-flag-fill text-warning me-1"></i>
                  Marcar como hito importante
                </>
              }
              checked={formData.isMilestone || formData.changeType === 'milestone_reached'}
              onChange={(e) => handleChange('isMilestone', e.target.checked)}
              disabled={isSubmitting || formData.changeType === 'milestone_reached'}
            />
            <Form.Text className="text-muted">
              Los hitos se destacan en la bitácora y pueden filtrarse fácilmente.
            </Form.Text>
          </Form.Group>

          {/* Upload Progress */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="mt-3">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {uploadProgress}%
                </div>
              </div>
              <small className="text-muted">Subiendo imagen...</small>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                {selectedFile ? 'Subiendo...' : 'Guardando...'}
              </>
            ) : (
              <>
                <i className={`bi ${selectedFile ? 'bi-upload' : 'bi-plus-circle'} me-1`}></i>
                {selectedFile ? 'Subir Foto' : 'Agregar Nota'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddManualEventModal;
